import { Button, Callout, Flex, Link, Text, TextField } from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import { Formik } from "formik";
import { useMemo, useState } from "react";
import { Navigate, NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AUTH_STATION_SLUG_HEADER } from "use-smart-locks-shared";
import * as Yup from "yup";
import { routes } from "../../../routes";
import { FullSpinner } from "../../../shared/components/full-spinner";
import { AuthRoot } from "../auth-root.component";
import { authClient } from "../client/auth-client";
import {
  getErrorMessage,
  isEmailNotVerifiedError,
} from "../client/auth-client-errors";
import { buildRegisterRedirectPath } from "../client/login-redirect";
import { AuthErrorMessage } from "../components/auth-error-message.component";
import {
  createEmailValidation,
  passwordRequiredValidation,
} from "../validation/auth-validation";
import {
  getAuthStationRestrictionMessage,
  useAuthPageContext,
} from "./use-auth-page-context";
import { useIsLoggedInWithStoredToken } from "./use-is-logged-in-with-stored-token";

type LoginInput = {
  email: string;
  password: string;
  fetchOptions?: Parameters<typeof authClient.signIn.email>[1];
};

export function LoginPage() {
  const navigate = useNavigate();
  const shouldRedirectAuthenticatedUser = useIsLoggedInWithStoredToken();
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState("");
  const {
    nextPath,
    stationSlug,
    allowedEmailDomains,
    isLoadingStationContext,
    stationContextErrorMessage,
  } = useAuthPageContext();
  const loginValidationSchema = useMemo(
    () =>
      Yup.object({
        email: createEmailValidation(allowedEmailDomains),
        password: passwordRequiredValidation,
      }),
    [allowedEmailDomains],
  );
  const loginMutation = useMutation({
    mutationFn: async ({ fetchOptions, ...input }: LoginInput) => {
      await authClient.signIn.email(input, fetchOptions);
      // required to avoid race condition with cached session in RequiresAuthentication component
      await authClient.getSession();
    },
    onSuccess: () => {
      void navigate(nextPath, { replace: true });
    },
  });
  const resendVerificationMutation = useMutation({
    mutationFn: async (input: { email: string }) => {
      await authClient.sendVerificationEmail(input);
    },
    onSuccess: () => {
      toast.success("Bestätigungs-E-Mail wurde erneut gesendet.");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const showResendVerificationButton =
    loginMutation.isError && isEmailNotVerifiedError(loginMutation.error);

  if (shouldRedirectAuthenticatedUser) {
    return <Navigate to={nextPath} replace />;
  }

  if (isLoadingStationContext) {
    return (
      <AuthRoot title="Einloggen">
        <FullSpinner />
      </AuthRoot>
    );
  }

  return (
    <AuthRoot title="Einloggen">
      {allowedEmailDomains.length > 0 && (
        <Callout.Root color="blue">
          <Callout.Text>
            {getAuthStationRestrictionMessage(allowedEmailDomains)}
          </Callout.Text>
        </Callout.Root>
      )}
      {stationContextErrorMessage && (
        <AuthErrorMessage message={stationContextErrorMessage} />
      )}
      <Formik
        initialValues={{ email: "", password: "" }}
        validationSchema={loginValidationSchema}
        validateOnMount
        onSubmit={(values) => {
          setLastSubmittedEmail(values.email);
          resendVerificationMutation.reset();
          loginMutation.mutate({
            email: values.email,
            password: values.password,
            fetchOptions: stationSlug
              ? {
                  headers: {
                    [AUTH_STATION_SLUG_HEADER]: stationSlug,
                  },
                }
              : undefined,
          });
        }}
      >
        {({
          values,
          errors,
          touched,
          isValid,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="3">
              <Flex direction="column" gap="1">
                <Text as="label" size="2" weight="medium" htmlFor="login-email">
                  E-Mail
                </Text>
                <TextField.Root
                  id="login-email"
                  required
                  type="email"
                  size="3"
                  placeholder="E-Mail"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.email && errors.email && (
                  <Text size="1" color="red">
                    {errors.email}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap="1">
                <Text
                  as="label"
                  size="2"
                  weight="medium"
                  htmlFor="login-password"
                >
                  Passwort
                </Text>
                <TextField.Root
                  id="login-password"
                  required
                  type="password"
                  size="3"
                  placeholder="Passwort"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.password && errors.password && (
                  <Text size="1" color="red">
                    {errors.password}
                  </Text>
                )}
              </Flex>
              <Button
                type="submit"
                size="3"
                variant="soft"
                loading={loginMutation.isPending}
                disabled={
                  !isValid ||
                  loginMutation.isPending ||
                  !!stationContextErrorMessage
                }
              >
                Einloggen
              </Button>
            </Flex>
          </form>
        )}
      </Formik>
      <Flex justify="between" align="center">
        <Link asChild>
          <NavLink to={routes.auth.requestPasswordReset}>
            Passwort vergessen
          </NavLink>
        </Link>
        <Link asChild>
          <NavLink to={buildRegisterRedirectPath(nextPath, stationSlug)}>
            Neues Konto erstellen
          </NavLink>
        </Link>
      </Flex>
      {loginMutation.isError && (
        <AuthErrorMessage error={loginMutation.error} />
      )}
      {showResendVerificationButton && (
        <Flex direction="column" gap="2" align="start">
          <Button
            type="button"
            variant="ghost"
            disabled={
              resendVerificationMutation.isPending || !lastSubmittedEmail
            }
            loading={resendVerificationMutation.isPending}
            onClick={() => {
              resendVerificationMutation.mutate({
                email: lastSubmittedEmail,
              });
            }}
          >
            Bestätigungs-E-Mail erneut senden
          </Button>
        </Flex>
      )}
    </AuthRoot>
  );
}
